import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { buildShopifyManagedPricingUrl } from "@/lib/appLinks";
import { usesWalletBillingForMerchant } from "@/lib/billing";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowRight,
  Check,
  Gift,
  Loader2,
  Sparkles,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/currency";
import { getShopifyEmbeddedContext, openShopifyTopLevel } from "@/lib/shopifyEmbedded";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabaseFunctionErrors";
import { invokeShopifyEmbeddedFunction } from "@/lib/shopifySessionToken";

interface Props {
  billingProvider?: "shopify" | "razorpay" | string | null;
  merchantId?: string | null;
  currentPlan: string;
  currentStatus?: string;
  platformType?: string | null;
  signupSource?: string | null;
  onSuccess: () => void;
}

interface CreateSubscriptionOrderResponse {
  amount?: number;
  billingProvider?: "shopify" | "razorpay";
  currency?: string;
  error?: string;
  invoiceNumber?: string;
  keyId?: string;
  planName?: string;
  razorpayOrderId?: string;
  storeName?: string;
}

type ShopifyBillablePlan = "free" | "starter" | "growth";

const SHOPIFY_BILLING_PLANS: Array<{
  cta: string;
  description: string;
  features: string[];
  freeSplits: number;
  id: ShopifyBillablePlan;
  icon: typeof Sparkles;
  monthlyPrice: number;
  name: string;
  overageFeePercent: number;
  popular?: boolean;
}> = [
  {
    cta: "Activate Free",
    description: "Zero monthly fee after your 30-day intro trial",
    features: [
      "Starts after your 30-day intro trial",
      "5 free splits every billing cycle",
      "2% fee after the free split allowance",
      "Unlimited total splits",
      "Merchant dashboard and reminders",
    ],
    freeSplits: 5,
    id: "free",
    icon: Sparkles,
    monthlyPrice: 0,
    name: "Free",
    overageFeePercent: 2,
  },
  {
    cta: "Choose Starter",
    description: "For stores that need lower fees",
    features: [
      "30-day intro trial with 100 free splits first",
      "Then $11.99/month",
      "25 free splits every billing cycle",
      "1.5% fee after the free split allowance",
      "Unlimited total splits",
    ],
    freeSplits: 25,
    id: "starter",
    icon: Zap,
    monthlyPrice: 11.99,
    name: "Starter",
    overageFeePercent: 1.5,
  },
  {
    cta: "Choose Growth",
    description: "For stores doing high split volume",
    features: [
      "30-day intro trial with 100 free splits first",
      "Then $29.99/month",
      "100 free splits every billing cycle",
      "1% fee after the free split allowance",
      "Unlimited total splits",
    ],
    freeSplits: 100,
    id: "growth",
    icon: Gift,
    monthlyPrice: 29.99,
    name: "Growth",
    overageFeePercent: 1,
    popular: true,
  },
];

export function SubscriptionPayment({
  billingProvider,
  currentPlan,
  currentStatus,
  platformType,
  signupSource,
  onSuccess,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState<ShopifyBillablePlan>("growth");
  const [processing, setProcessing] = useState(false);
  const processedBillingReturn = useRef(false);

  const selectedPlanData = useMemo(
    () => SHOPIFY_BILLING_PLANS.find((plan) => plan.id === selectedPlan) || SHOPIFY_BILLING_PLANS[0],
    [selectedPlan],
  );
  const embeddedContext = getShopifyEmbeddedContext();
  const shouldForceShopifyManagedBilling = platformType === "shopify" && embeddedContext.embedded;
  const usesRazorpayBilling = !shouldForceShopifyManagedBilling && usesWalletBillingForMerchant({
    billing_provider: billingProvider,
    platform_type: platformType,
    signup_source: signupSource,
  });

  useEffect(() => {
    if (processedBillingReturn.current) {
      return;
    }

    if (searchParams.get("billing_return") !== "1") {
      return;
    }

    processedBillingReturn.current = true;
    setProcessing(true);

    const planFromReturn = searchParams.get("billing_plan") as ShopifyBillablePlan | null;

    const syncSubscription = async () => {
      try {
        const { data, error } = await invokeShopifyEmbeddedFunction<{
          billingProvider?: string;
          error?: string;
          plan?: string;
          success?: boolean;
        }>("verify-subscription-payment", {
          body: {
            plan: planFromReturn || undefined,
          },
        });

        if (error) {
          throw error;
        }

        if (data?.success !== false) {
          toast.success("Plan activated!", {
            description: `Your ${data?.plan || planFromReturn || "selected"} plan is now active.`,
          });
        }

        onSuccess();

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("billing_return");
        nextParams.delete("billing_plan");
        setSearchParams(nextParams, { replace: true });
      } catch (error: unknown) {
        console.error("Error syncing Shopify billing:", error);
        const message = await getSupabaseFunctionErrorMessage(error, "Failed to verify Shopify billing");
        toast.error(message);
      } finally {
        setProcessing(false);
      }
    };

    syncSubscription();
  }, [onSuccess, searchParams, setSearchParams]);

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if ((window as any).Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleActivatePlan = async () => {
    if (!usesRazorpayBilling) {
      const managedPricingUrl = buildShopifyManagedPricingUrl(embeddedContext.shop);
      openShopifyTopLevel(managedPricingUrl);
      return;
    }

    setProcessing(true);

    try {
      const { data, error } = await invokeShopifyEmbeddedFunction<CreateSubscriptionOrderResponse>("create-subscription-order", {
        body: {
          plan: selectedPlan,
          billingCycle: "monthly",
        },
      });

      if (error) {
        throw error;
      }

      if (data?.billingProvider === "razorpay") {
        if (!data.razorpayOrderId || !data.keyId || !data.amount) {
          toast.success("Plan activated!");
          onSuccess();
          setProcessing(false);
          return;
        }

        const loaded = await loadRazorpayScript();
        if (!loaded) {
          throw new Error("Unable to load Razorpay checkout");
        }

        const razorpay = new (window as any).Razorpay({
          amount: Math.round(data.amount * 100),
          currency: data.currency || "INR",
          description: data.planName || selectedPlanData.name,
          handler: async (response: any) => {
            try {
              const verifyResult = await invokeShopifyEmbeddedFunction<{
                error?: string;
                plan?: string;
              }>("verify-subscription-payment", {
                body: {
                  plan: selectedPlan,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                },
              });

              if (verifyResult.error) {
                throw verifyResult.error;
              }

              toast.success("Plan activated!", {
                description: `Your ${verifyResult.data?.plan || selectedPlan} plan is now active.`,
              });
              onSuccess();
            } catch (verifyError) {
              console.error("Razorpay subscription verification error:", verifyError);
              const message = await getSupabaseFunctionErrorMessage(
                verifyError,
                "Payment received but subscription verification failed",
              );
              toast.error(message);
            } finally {
              setProcessing(false);
            }
          },
          key: data.keyId,
          name: "Splitkart",
          notes: {
            invoice_number: data.invoiceNumber || "",
            plan: selectedPlan,
          },
          order_id: data.razorpayOrderId,
          prefill: {
            name: data.storeName || "",
          },
          theme: {
            color: "#3F51B5",
          },
        });

        razorpay.on("payment.failed", (response: any) => {
          toast.error(response?.error?.description || "Razorpay payment failed");
          setProcessing(false);
        });

        razorpay.open();
        return;
      }

      throw new Error(data?.error || "Unexpected billing provider response");
    } catch (error: unknown) {
      console.error("Billing approval error:", error);
      const message = await getSupabaseFunctionErrorMessage(error, "Failed to start Shopify billing approval");
      toast.error(message);
      setProcessing(false);
    }
  };

  const hasActivePaidOrFreePlan = currentStatus === "active";
  const isCurrentPlan = hasActivePaidOrFreePlan && currentPlan === selectedPlan;

  const currentPlanLabel = useMemo(() => {
    if (currentStatus === "canceled" || currentStatus === "past_due" || currentStatus === "paused") {
      return usesRazorpayBilling ? "approval required" : "managed in Shopify";
    }

    if (currentPlan === "trial_expired") {
      return "trial expired";
    }

    return currentPlan;
  }, [currentPlan, currentStatus]);

  const selectedPlanIsCurrent = hasActivePaidOrFreePlan && currentPlan === selectedPlan;

  return (
    <div className="space-y-6">
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-5">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-medium text-primary">
                {usesRazorpayBilling ? "Splitkart Billing" : "Shopify Billing"}
              </p>
              <h3 className="text-xl font-semibold">
                {usesRazorpayBilling ? "Choose your live plan" : "Plans managed in Shopify"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {usesRazorpayBilling
                  ? "WooCommerce and direct Shopify merchants pay subscription and overage fees through the Splitkart wallet and checkout flow."
                  : "Shopify App Store merchants choose and change plans in Shopify. Splitkart only reads the active plan and bills overage commissions through Shopify after the free split allowance is used."}
              </p>
            </div>
            <Badge variant="outline" className="w-fit">
              Current plan: {currentPlanLabel}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <div id="plan-selection" className="grid gap-4">
        {SHOPIFY_BILLING_PLANS.map((plan) => {
          const isSelected = selectedPlan === plan.id;
          const displayPrice = formatCurrency(plan.monthlyPrice, "USD");

          return (
            <Card
              key={plan.id}
              className={`transition-all ${
                isSelected ? "border-primary ring-2 ring-primary/20" : "hover:border-primary/50"
              } ${hasActivePaidOrFreePlan && currentPlan === plan.id ? "bg-primary/5" : ""}`}
              onClick={() => setSelectedPlan(plan.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div
                      className={`rounded-lg p-2 ${
                        isSelected ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"
                      }`}
                    >
                      <plan.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{plan.name}</h3>
                        {plan.popular && <Badge className="bg-primary/10 text-primary">Popular</Badge>}
                        {hasActivePaidOrFreePlan && currentPlan === plan.id && <Badge variant="outline">Current</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">{plan.description}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {plan.features.map((feature) => (
                          <span key={feature} className="rounded bg-muted px-2 py-1 text-xs">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-2xl font-bold">{displayPrice}</div>
                    <div className="text-sm text-muted-foreground">/month</div>
                    <div className="mt-1 text-xs text-primary">
                      {plan.freeSplits} free splits, then {plan.overageFeePercent}%
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="font-medium">{selectedPlanData.name} Plan</p>
              <p className="text-sm text-muted-foreground">
                {selectedPlanData.id === "free"
                  ? `After your 30-day intro trial, ${selectedPlanData.freeSplits} free splits each billing cycle, then ${selectedPlanData.overageFeePercent}% on completed split order value`
                  : `30-day intro trial first, then ${formatCurrency(selectedPlanData.monthlyPrice, "USD")} monthly with ${selectedPlanData.freeSplits} free splits each billing cycle and ${selectedPlanData.overageFeePercent}% after the allowance`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">{formatCurrency(selectedPlanData.monthlyPrice, "USD")}</p>
              <p className="text-sm text-muted-foreground">monthly</p>
            </div>
          </div>

          <Button
            className="w-full"
            disabled={processing || (usesRazorpayBilling && isCurrentPlan)}
            onClick={handleActivatePlan}
            size="lg"
          >
            {processing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {usesRazorpayBilling ? "Opening billing checkout..." : "Opening Shopify pricing..."}
              </>
            ) : !usesRazorpayBilling ? (
              <>
                {selectedPlanIsCurrent ? "Manage current plan in Shopify" : `Manage ${selectedPlanData.name} in Shopify`}
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            ) : isCurrentPlan ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Current Plan
              </>
            ) : (
              <>
                {selectedPlanData.cta}
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
